import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Utensils, Wine, Coffee, Palmtree, Music, CalendarDays, Repeat, Star, ArrowRight, Sparkles } from "lucide-react";
import Layout from "../components/Layout";
import SearchBar from "../components/SearchBar";
import VenueCard from "../components/VenueCard";
import CoastalConditions from "../components/CoastalConditions";
import NewsletterForm from "../components/NewsletterForm";
import { SkeletonGrid, SkeletonEventCard, SkeletonStoryCard } from "../components/Skeleton";
import { api } from "../lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1671769336265-6de857f56f6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxhaXJsaWUlMjBiZWFjaCUyMHF1ZWVuc2xhbmR8ZW58MHx8fHwxNzc3MDM4OTUzfDA&ixlib=rb-4.1.0&q=85";

const CATS = [
  { slug: "restaurant", label: "Restaurants", Icon: Utensils, blurb: "Seafood, local, global" },
  { slug: "bar", label: "Bars", Icon: Wine, blurb: "Cocktails & sunsets" },
  { slug: "cafe", label: "Cafés", Icon: Coffee, blurb: "Coffee, brunch, chill" },
  { slug: "nightlife", label: "Partys", Icon: Music, blurb: "Late nights & dance floors" },
];

// Adapts the "Top picks" heading and the daily rotating pick to the current time of day.
// Note: café slot runs until noon so 11am -> 12pm still shows cafés + lunch spots.
function timeContext(d = new Date()) {
  const h = d.getHours();
  if (h < 6)  return { slug: "bar",        label: "Late-night in Airlie",       eyebrow: "Right now",         pickLabel: "Tonight's late spot",     pickBlurb: "Still going strong" };
  if (h < 12) return { slug: "cafe",       label: "Morning cafés open now",     eyebrow: "This morning",      pickLabel: "Today's coffee pick",     pickBlurb: "The one to start your day" };
  if (h < 15) return { slug: "restaurant", label: "Lunch picks right now",      eyebrow: "Lunchtime",         pickLabel: "Today's lunch spot",      pickBlurb: "Where the locals eat" };
  if (h < 17) return { slug: "cafe",       label: "Afternoon coffee & catch-up",eyebrow: "This afternoon",    pickLabel: "Today's afternoon pick",  pickBlurb: "A slower kind of pause" };
  if (h < 20) return { slug: "bar",        label: "Sundowners in Airlie",       eyebrow: "Sundowner o'clock", pickLabel: "Today's after-work beer", pickBlurb: "First round, salt air" };
  if (h < 23) return { slug: "restaurant", label: "Dinner picks right now",     eyebrow: "Dinnertime",        pickLabel: "Today's dinner spot",     pickBlurb: "Tonight's plate" };
  return       { slug: "nightlife",  label: "Late-night in Airlie",       eyebrow: "Late night",        pickLabel: "Tonight's nightcap",      pickBlurb: "One more before bed" };
}

function dailySeed(d = new Date()) {
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}

// "Dining" in the venue name signals a dinner-only fine-dining focus, so keep
// them out of lunch recommendations.
const isDiningVenue = (v) => (v?.name || "").toLowerCase().includes("dining");

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [dailyPick, setDailyPick] = useState(null);
  const [news, setNews] = useState(null);
  const [events, setEvents] = useState(null);
  const ctx = useMemo(() => timeContext(), []);

  useEffect(() => {
    // Prioritise venues that match the current time slot's vibe (café in the morning,
    // bar around sundowner, restaurant at meal times), then fall back to any open venue.
    const isPickable = (v) => v.category && v.category !== "activity";
    const hour = new Date().getHours();
    const isLunch = hour >= 11 && hour < 15;
    // Pre-noon: also surface lunch-friendly restaurants (not dinner-only "Dining" places).
    const preNoonMixesLunch = hour < 12;

    const preferredUrl = `/venues?open_now=true&category=${ctx.slug}`;
    const extras = preNoonMixesLunch ? [api.get(`/venues?open_now=true&category=restaurant`)] : [];

    Promise.all([
      api.get(preferredUrl),
      api.get(`/venues?open_now=true`),
      ...extras,
    ]).then(([pref, all, lunchExtras]) => {
      let preferred = (pref.data || []).filter(isPickable);
      let anyOpen = (all.data || []).filter(isPickable);
      // Around lunchtime, dinner-only "Dining" venues never belong in the picks.
      if (isLunch) {
        preferred = preferred.filter((v) => !isDiningVenue(v));
        anyOpen = anyOpen.filter((v) => !isDiningVenue(v));
      }
      // Before noon, blend cafés with lunch-friendly restaurants (still excluding "Dining").
      if (preNoonMixesLunch && lunchExtras) {
        const lunchOpen = (lunchExtras.data || [])
          .filter(isPickable)
          .filter((v) => !isDiningVenue(v));
        preferred = [
          ...preferred,
          ...lunchOpen.filter((v) => !preferred.some((p) => p.id === v.id)),
        ];
      }
      const merged = [
        ...preferred,
        ...anyOpen.filter((v) => !preferred.some((p) => p.id === v.id)),
      ];
      if (!merged.length) {
        api.get("/venues").then((r2) =>
          setFeatured((r2.data || []).filter(isPickable).slice(0, 3)),
        );
        setDailyPick(null);
        return;
      }
      // Daily rotating pick — deterministic per calendar day so it stays stable through
      // the current time slot instead of reshuffling on every reload.
      const source = preferred.length ? preferred : merged;
      const pick = source[dailySeed() % source.length];
      setDailyPick(pick);
      // Show 3 supporting venues (1 row) after the daily pick, excluding the pick itself.
      // Before noon we stay within the café + lunch-friendly restaurant pool.
      const supporting = preNoonMixesLunch ? preferred : merged;
      setFeatured(supporting.filter((v) => v.id !== pick.id).slice(0, 3));
    });
    api.get("/news").then((r) => setNews(r.data.slice(0, 3)));
    api.get("/events").then((r) => setEvents(r.data.slice(0, 3)));
  }, [ctx]);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden -mt-20 pt-20">
        <img src={HERO_BG} alt="Airlie Beach" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full grain">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-sponsored text-sm tracking-[0.2em] uppercase mb-4" data-testid="hero-tagline">
              Whitsundays · Queensland · Since 2026
            </span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight leading-[0.95] mb-6" data-testid="hero-heading">
              Everything worth doing<br />
              <span className="italic font-normal text-sponsored">in Airlie Beach.</span>
            </h1>
            <p className="text-white/85 text-lg max-w-xl mb-10 leading-relaxed">
              The local's guide to every restaurant, bar, café, reef trip and beach party from Cannonvale to Shute Harbour. Curated. Searchable. Free.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-4"
          >
            <SearchBar />
            <CoastalConditions />
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-sm tracking-widest text-coastal uppercase">Explore</span>
            <h2 className="font-heading text-3xl lg:text-5xl text-navy tracking-tight mt-2">What are you after?</h2>
          </div>
          <Link to="/directory/all" className="hidden md:flex items-center gap-2 text-navy hover:gap-3 transition-all" data-testid="explore-all-link">
            Browse all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATS.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
            >
              <Link
                to={`/directory/${c.slug}`}
                className="card-hover block h-64 rounded-2xl p-6 bg-white border border-black/5 relative overflow-hidden group"
                data-testid={`category-${c.slug}`}
              >
                <c.Icon className="w-10 h-10 text-navy mb-4" strokeWidth={1.25} />
                <h3 className="font-heading text-2xl text-navy">{c.label}</h3>
                <p className="text-neutral-500 mt-1">{c.blurb}</p>
                <ArrowRight className="absolute bottom-6 right-6 w-5 h-5 text-navy/40 group-hover:text-navy group-hover:translate-x-1 transition-all" />
                <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-sand/30 group-hover:bg-coastal/10 transition-colors" />
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED (incl. sponsored) */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-sm tracking-widest text-coastal uppercase">{ctx.eyebrow}</span>
              <h2 className="font-heading text-3xl lg:text-5xl text-navy tracking-tight mt-2" data-testid="top-picks-heading">{ctx.label}</h2>
            </div>
          </div>

          {/* Daily rotating pick — themed to the current time slot */}
          {dailyPick ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <Link
                to={`/venue/${dailyPick.id}`}
                data-testid="daily-pick-card"
                className="group grid md:grid-cols-[280px_1fr] gap-0 rounded-2xl overflow-hidden border border-black/5 bg-cream/60 hover:shadow-lg transition-shadow"
              >
                <div className="relative h-56 md:h-full bg-sand/40 overflow-hidden">
                  {dailyPick.places_photo_url || dailyPick.images?.[0] ? (
                    <img
                      src={dailyPick.places_photo_url || dailyPick.images[0]}
                      alt={dailyPick.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-sand/60 to-coastal/20" />
                  )}
                  <div className="absolute top-4 left-4 bg-navy text-white text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full flex items-center gap-1.5">
                    <Sparkles className="w-3 h-3" /> {ctx.pickLabel}
                  </div>
                </div>
                <div className="p-6 md:p-8 flex flex-col justify-center">
                  <div className="text-[10px] tracking-widest text-coastal uppercase">{ctx.pickBlurb}</div>
                  <h3 className="font-heading text-2xl lg:text-3xl text-navy mt-1">{dailyPick.name}</h3>
                  <p className="text-neutral-500 mt-2 line-clamp-2">{dailyPick.description}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-navy font-medium">
                    Open the venue <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ) : null}

          <div className="grid md:grid-cols-3 gap-6">
            {featured === null
              ? <SkeletonGrid count={3} />
              : featured.map((v, i) => <VenueCard key={v.id} venue={v} index={i} />)}
          </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-sm tracking-widest text-coastal uppercase">What's on</span>
            <h2 className="font-heading text-3xl lg:text-5xl text-navy tracking-tight mt-2">Upcoming events</h2>
          </div>
          <Link to="/events" className="text-navy hover:gap-3 flex items-center gap-2 transition-all">All events <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {events === null ? (
            <>
              <SkeletonEventCard />
              <SkeletonEventCard />
              <SkeletonEventCard />
            </>
          ) : events.map((e, i) => (
            <Link
              key={e.id}
              to={`/events/${e.id}`}
              className="block card-hover rounded-2xl overflow-hidden bg-white border border-black/5 shadow-sm"
              data-testid={`event-card-${e.id}`}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
              <div className="aspect-[16/10] overflow-hidden bg-sand/30 relative">
                {e.image && <img src={e.image} alt={e.title} className="w-full h-full object-cover" />}
                {e.editors_pick && (
                  <span className="absolute top-3 right-3 bg-sponsored text-navy text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-semibold shadow">
                    <Star className="w-3 h-3 fill-navy" /> Editor's pick
                  </span>
                )}
                {e.is_recurring && (
                  <span className="absolute top-3 left-3 bg-coastal text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                    <Repeat className="w-3 h-3" />
                    {e.recurrence === "weekly" ? "Every week" : e.recurrence === "biweekly" ? "Every 2 weeks" : "Monthly"}
                  </span>
                )}
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-coastal mb-2">
                  <CalendarDays className="w-4 h-4" />
                  {e.is_recurring && <span className="text-neutral-500">Next:</span>}
                  {new Date(e.start_time).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                </div>
                <h3 className="font-heading text-xl text-navy mb-2">{e.title}</h3>
                <p className="text-sm text-neutral-500 line-clamp-2">{e.description}</p>
                <div className="flex items-center justify-between mt-4 text-xs text-neutral-500">
                  <span>{e.location}</span>
                  {e.price && <span className="font-medium text-navy">{e.price}</span>}
                </div>
              </div>
              </motion.div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEWS / STORIES */}
      <section className="bg-navy text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-sm tracking-widest text-sponsored uppercase">Stories</span>
              <h2 className="font-heading text-3xl lg:text-5xl tracking-tight mt-2">From the coast</h2>
            </div>
            <Link to="/news" className="text-sponsored hover:gap-3 flex items-center gap-2 transition-all">All stories <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {news === null ? (
              <>
                <SkeletonStoryCard />
                <SkeletonStoryCard />
                <SkeletonStoryCard />
              </>
            ) : news.map((n, i) => (
              <motion.div key={n.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={`/news/${n.slug || n.id}`} className="card-hover block rounded-2xl overflow-hidden bg-white text-navy" data-testid={`news-card-${n.slug || n.id}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-sand/30">
                    {n.cover_image && <img src={n.cover_image} alt={n.title} className="w-full h-full object-cover" />}
                  </div>
                  <div className="p-6">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {n.tags?.slice(0, 2).map((t) => <span key={t} className="text-[10px] tracking-widest uppercase text-coastal">{t}</span>)}
                    </div>
                    <h3 className="font-heading text-xl mb-2">{n.title}</h3>
                    <p className="text-sm text-neutral-500 line-clamp-3">{n.excerpt}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER SIGNUP */}
      <section className="bg-navy text-white py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block text-sm tracking-widest text-sponsored uppercase mb-4">The digest</span>
          <h2 className="font-heading text-3xl lg:text-5xl tracking-tight mb-4">
            This Week <span className="italic text-sponsored">in Airlie</span>
          </h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
            A short weekly note with the best events, new openings and quiet corners of the Whitsundays. Every Wednesday. Zero spam.
          </p>
          <NewsletterForm />
        </div>
      </section>

      {/* SPONSOR CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-gradient-to-br from-sand/60 via-white to-sand/40 rounded-3xl p-10 md:p-16 text-center border border-black/5">
          <span className="inline-block text-sm tracking-widest text-coastal uppercase mb-4">For venue owners</span>
          <h2 className="font-heading text-3xl lg:text-5xl text-navy tracking-tight mb-4 max-w-2xl mx-auto">
            Get your venue in front of thousands of Airlie visitors.
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-8">Free to list. Reach thousands of Airlie visitors looking for their next great meal, drink or activity.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/register" className="rounded-full bg-navy text-white px-6 py-3 hover:bg-navy/90 transition-colors" data-testid="cta-list-venue">
              List your venue free
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}

